import { ChangeDetectionStrategy, Component, input, linkedSignal, output } from "@angular/core";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { s25Resource } from "../../util/s25.resource";
import { applyEach, form, FormField, validate } from "@angular/forms/signals";
import { AngBridge } from "../../util/ang.bridge.util";
import { S25Util } from "../../util/s25-util";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";

@TypeManagerDecorator("s25-ng-custom-attribute-options")
@Component({
    selector: "s25-ng-custom-attribute-options",
    template: `
        @if (discreteOptions.resource.isLoading()) {
            <s25-ng-loading-inline-static [text]="'Loading Discrete Options'" />
        } @else if (discreteOptions.resource.error()) {
            Something went wrong when loading the discrete options.
        } @else {
            <div class="discrete-options-wrapper">
                <ol>
                    @for (option of form; track option().value()) {
                        <li class="c-margin-bottom--half">
                            <div class="option-container">
                                <input [formField]="option" class="c-input" />
                                <s25-ng-icon-button
                                    [type]="'arrowUp'"
                                    [ariaLabel]="'Move option up'"
                                    (clicked)="moveUp($index)"
                                />
                                <s25-ng-icon-button
                                    [type]="'arrowDown'"
                                    [ariaLabel]="'Move option down'"
                                    (clicked)="moveDown($index)"
                                />
                                <s25-ng-icon-button
                                    [type]="'close'"
                                    [ariaLabel]="'Remove option'"
                                    (clicked)="removeOption($index)"
                                />
                            </div>
                            @for (error of option().errors(); track error) {
                                <p class="error-message">{{ error.message }}</p>
                            }
                        </li>
                    }
                </ol>
                <div class="buttons">
                    <s25-ng-button [type]="'outline'" (clicked)="addOption()">Add Option</s25-ng-button>
                </div>
            </div>
            <div class="buttons">
                <s25-ng-button [type]="'primary'" [disabled]="!form().valid()" (clicked)="save()">Save</s25-ng-button>
            </div>
        }
    `,
    styles: `
        .discrete-options-wrapper {
            width: fit-content;
            margin: 0 auto 1em;
        }

        .option-container {
            display: flex;
            gap: 5px;
        }

        .error-message {
            color: var(--error-message-color);
            font-size: 0.9em;
            padding-block-start: 0.25rem;
        }

        .buttons {
            display: flex;
            justify-content: flex-end;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25LoadingInlineStaticComponent, S25IconButtonComponent, FormField, S25ButtonComponent],
})
export class S25CustomAttributeOptionsComponent extends AngBridge {
    readonly attrId = input.required<number>();
    readonly saved = output<void>();

    readonly discreteOptions = s25Resource({
        params: () => this.attrId(),
        loader: ({ params: attrId, abortSignal }) => {
            return CustomAttributeService.getDiscreteOptions(attrId);
        },
    });

    readonly model = linkedSignal(() => this.discreteOptions.value() ?? []);
    readonly form = form(this.model, (schemaPath) => {
        applyEach(schemaPath, (option) => {
            validate(option, (field) => {
                if (field.value()?.length > 80) {
                    return { kind: "error", message: "Option cannot be longer than 80 characters" };
                }
            });
        });
    });

    async save() {
        const options = S25Util.array
            .unique(this.model())
            .filter((o) => !!o)
            .map((o, i) => ({ value: o, sort_order: i }));

        const resp = await CustomAttributeService.putDiscreteOptions(this.attrId(), options);
        if (!S25Util.isDefined(resp?.root)) alert("Something went wrong when saving discrete options");
        else this.saved.emit();
    }

    addOption() {
        this.model.update((options) => [...options, ""]);
    }

    removeOption(index: number) {
        this.model.update((options) => options.filter((_, i) => i !== index));
    }

    moveUp(index: number) {
        if (index <= 0) return;
        this.model.update((options) => {
            const copy = [...options];
            [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
            return copy;
        });
    }

    moveDown(index: number) {
        if (index >= this.model().length - 1) return;
        this.model.update((options) => {
            const copy = [...options];
            [copy[index], copy[index + 1]] = [copy[index + 1], copy[index]];
            return copy;
        });
    }
}
